import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import NotificationCenter from './NotificationCenter';
import GlobalSearch from './GlobalSearch';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { messagingService } from '../../services/messagingService';
import CartDrawer from '../../pages/official-store/components/CartDrawer';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [messageNotificationCount, setMessageNotificationCount] = useState(0);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  const { user, userProfile, isAuthenticated, signOut: authSignOut } = useAuth();
  const { cartItemCount, openCart, isCartOpen, closeCart, cartItems, removeFromCart, updateQuantity } = useCart();

  const userRole = userProfile?.role || localStorage.getItem('userRole') || 'user';

  // Fetch unread message count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!isAuthenticated) {
        setMessageNotificationCount(0);
        return;
      }

      try {
        const count = await messagingService?.getTotalUnreadCount();
        setMessageNotificationCount(count);
      } catch (error) {
        // Silently ignore network errors for non-critical badge count
      }
    };

    fetchUnreadCount();

    // Refresh count every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Real-time subscription for new messages
  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      return;
    }

    console.log('🔔 Setting up real-time message notification subscription');

    const subscription = messagingService?.subscribeToNewMessages(
      user?.id,
      async (message) => {
        // Only increment if message is not from current user
        if (message?.senderId !== user?.id) {
          console.log('📬 New message received, updating badge');
          // Refresh the count
          try {
            const count = await messagingService?.getTotalUnreadCount();
            setMessageNotificationCount(count);
          } catch (error) {
            // Silently ignore network errors for non-critical badge count
          }
        }
      }
    );

    return () => {
      if (subscription) {
        console.log('🔕 Cleaning up message notification subscription');
        subscription?.unsubscribe();
      }
    };
  }, [isAuthenticated, user?.id]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location?.pathname]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const navigationItems = [
    {
      label: 'Etkinlikler',
      path: '/event-discovery',
      icon: 'Calendar',
      roles: ['user', 'organizer', 'admin']
    },
    {
      label: 'Spot Rehberi',
      path: '/spot-guide',
      icon: 'MapPin',
      roles: ['user', 'organizer', 'admin']
    },
    {
      label: 'Kullanıcı Pazarı',
      path: '/user-marketplace',
      icon: 'Store',
      roles: ['user', 'organizer', 'admin']
    },
    {
      label: 'Mağaza',
      path: '/official-store',
      icon: 'ShoppingBag',
      roles: ['user', 'organizer', 'admin']
    },
    {
      label: 'Blog',
      path: '/blog-listing',
      icon: 'BookOpen',
      roles: ['user', 'organizer', 'admin']
    },
    {
      label: 'Mesajlar',
      path: '/messaging-center',
      icon: 'MessageCircle',
      roles: ['user', 'organizer', 'admin'],
      hasNotification: true,
      requiresAuth: true
    },
    {
      label: 'Profil',
      path: '/user-profile',
      icon: 'User',
      roles: ['user', 'organizer', 'admin'],
      requiresAuth: true
    }
  ];

  const adminItem = {
    label: 'Yönetim',
    path: '/admin-dashboard',
    icon: 'Settings',
    roles: ['admin']
  };

  const filteredNavItems = navigationItems?.filter(item => {
    if (item?.requiresAuth && !isAuthenticated) {
      return false;
    }
    return item?.roles?.includes(userRole);
  });

  const showAdminLink = userRole === 'admin';

  const isActive = (path) => {
    return location?.pathname === path;
  };

  const handleMobileToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleLinkClick = () => {
    setIsMobileMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      await authSignOut();
      setShowUserMenu(false);
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const handleProfileClick = () => {
    setShowUserMenu(false);
    navigate('/user-profile');
  };

  return (
    <>
      <header className="header">
        <div className="header-container">
          <Link to="/" className="header-logo">
            <div className="header-logo-icon">
              <Icon name="Waves" size={20} color="currentColor" />
            </div>
            <span className="header-logo-text">PaddleTurkiye</span>
          </Link>

          {/* Global Search Trigger Button - Desktop */}
          <div className="hidden lg:flex flex-1 max-w-xl mx-8">
            <button 
              onClick={() => setIsSearchOpen(true)} 
              className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-500 px-4 py-2 rounded-full transition-colors w-full md:w-64"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" /></svg>
              <span className="text-sm font-medium">Ara...</span>
            </button>
          </div>

          <nav className="header-nav">
            {filteredNavItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`header-nav-link ${isActive(item?.path) ? 'active' : ''}`}
              >
                <Icon name={item?.icon} size={18} />
                <span>{item?.label}</span>
                {item?.hasNotification && messageNotificationCount > 0 && (
                  <span className="header-notification-badge">
                    <span className="header-notification-count">{messageNotificationCount}</span>
                  </span>
                )}
              </Link>
            ))}
            {showAdminLink && (
              <Link
                to={adminItem?.path}
                className={`header-nav-link ${isActive(adminItem?.path) ? 'active' : ''}`}
              >
                <Icon name={adminItem?.icon} size={18} />
                <span>{adminItem?.label}</span>
              </Link>
            )}
          </nav>

          <div className="header-actions">
            <div className="flex">
              <NotificationCenter />
            </div>
            
            <button
              onClick={() => isCartOpen ? closeCart() : openCart()}
              className="relative flex items-center justify-center w-10 h-10 rounded-lg hover:bg-muted transition-colors"
              aria-label="Sepeti aç"
            >
              <Icon name="ShoppingCart" size={20} />
              {cartItemCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">
                  {cartItemCount}
                </span>
              )}
            </button>
            
            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-500 text-white hover:from-blue-600 hover:to-cyan-600 transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  <Icon name="User" size={18} />
                  <span className="hidden md:inline font-medium">{user?.name || 'Kullanıcı'}</span>
                  <Icon name="ChevronDown" size={16} />
                </button>
                
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-semibold text-gray-900">{user?.name}</p>
                      <p className="text-xs text-gray-500 mt-1">{user?.email}</p>
                    </div>
                    <button
                      onClick={handleProfileClick}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                    >
                      <Icon name="User" size={16} />
                      <span>Profilim</span>
                    </button>
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        navigate('/user-profile#orders');
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                    >
                      <Icon name="Package" size={16} />
                      <span>Siparişlerim</span>
                    </button>
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        navigate('/my-tickets');
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                    >
                      <Icon name="Ticket" size={16} />
                      <span>Biletlerim</span>
                    </button>
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        navigate('/my-favorites');
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                    >
                      <Icon name="Heart" size={16} />
                      <span>Favorilerim</span>
                    </button>
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        navigate('/support-ticket-system-user-interface');
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                    >
                      <Icon name="Headphones" size={16} />
                      <span>Destek</span>
                    </button>
                    {userRole === 'admin' && (
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/support-ticket-system-admin-panel');
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                      >
                        <Icon name="Shield" size={16} />
                        <span>Destek Yönetimi</span>
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        navigate('/user-profile');
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                    >
                      <Icon name="Settings" size={16} />
                      <span>Ayarlar</span>
                    </button>
                    <div className="border-t border-gray-100 mt-2 pt-2">
                      <button
                        onClick={handleLogout}
                        className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors"
                      >
                        <Icon name="LogOut" size={16} />
                        <span>Çıkış Yap</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={handleLogin}
                className="hidden md:flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-500 text-white hover:from-blue-600 hover:to-cyan-600 transition-all duration-200 shadow-md hover:shadow-lg font-medium"
              >
                <Icon name="LogIn" size={18} />
                <span>Giriş Yap</span>
              </button>
            )}
            <button
              className="header-mobile-toggle"
              onClick={handleMobileToggle}
              aria-label="Menüyü aç"
            >
              <Icon name="Menu" size={24} />
            </button>
          </div>
        </div>
      </header>
      
      <div className={`mobile-menu-overlay ${isMobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-menu-header" style={{padding: '16px 16px 0 16px', flexShrink: 0}}>
          <Link to="/" className="header-logo" onClick={handleLinkClick}>
            <div className="header-logo-icon">
              <Icon name="Waves" size={20} color="currentColor" />
            </div>
            <span className="header-logo-text">PaddleTurkiye</span>
          </Link>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { isCartOpen ? closeCart() : openCart(); handleLinkClick(); }}
              className="sm:hidden relative flex items-center justify-center w-10 h-10 rounded-lg hover:bg-muted transition-colors"
              aria-label="Sepeti aç"
            >
              <Icon name="ShoppingCart" size={20} />
              {cartItemCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">
                  {cartItemCount}
                </span>
              )}
            </button>
            <button
              className="mobile-menu-close"
              onClick={handleMobileToggle}
              aria-label="Menüyü kapat"
            >
              <Icon name="X" size={24} />
            </button>
          </div>
        </div>

        {/* Scrollable content area */}
        <div style={{flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '0 16px 40px 16px'}}>
          {/* Global Search Trigger - Mobile */}
          <div className="py-3 border-b border-gray-200">
            <button 
              onClick={() => setIsSearchOpen(true)} 
              className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-500 px-4 py-2 rounded-full transition-colors w-full"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" /></svg>
              <span className="text-sm font-medium">Ara...</span>
            </button>
          </div>

          <nav className="mobile-menu-nav" style={{marginTop: '8px'}}>
            {filteredNavItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`mobile-menu-link ${isActive(item?.path) ? 'active' : ''}`}
                onClick={handleLinkClick}
              >
                <Icon name={item?.icon} size={22} />
                <span>{item?.label}</span>
                {item?.hasNotification && messageNotificationCount > 0 && (
                  <span className="header-notification-badge">
                    <span className="header-notification-count">{messageNotificationCount}</span>
                  </span>
                )}
              </Link>
            ))}
            {showAdminLink && (
              <Link
                to={adminItem?.path}
                className={`mobile-menu-link ${isActive(adminItem?.path) ? 'active' : ''}`}
                onClick={handleLinkClick}
              >
                <Icon name={adminItem?.icon} size={22} />
                <span>{adminItem?.label}</span>
              </Link>
            )}
          </nav>
          
          <div className="py-4 border-t border-gray-200" style={{marginTop: '8px'}}>
            {isAuthenticated ? (
              <div className="space-y-1">
                {/* Account Section Heading */}
                <div className="flex items-center gap-3 px-4 py-2">
                  <div className="flex-1 h-px bg-gray-200"></div>
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Hesabım</span>
                  <div className="flex-1 h-px bg-gray-200"></div>
                </div>
                <div className="px-4 py-2 bg-gray-50 rounded-lg mb-1">
                  <p className="text-sm font-semibold text-gray-900">{user?.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{user?.email}</p>
                </div>
                <button
                  onClick={() => {
                    handleLinkClick();
                    navigate('/user-profile');
                  }}
                  className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 rounded-lg flex items-center gap-3 transition-colors"
                >
                  <Icon name="User" size={18} />
                  <span>Profilim</span>
                </button>
                <button
                  onClick={() => {
                    handleLinkClick();
                    navigate('/user-profile#orders');
                  }}
                  className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 rounded-lg flex items-center gap-3 transition-colors"
                >
                  <Icon name="Package" size={18} />
                  <span>Siparişlerim</span>
                </button>
                <button
                  onClick={() => {
                    handleLinkClick();
                    navigate('/my-tickets');
                  }}
                  className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 rounded-lg flex items-center gap-3 transition-colors"
                >
                  <Icon name="Ticket" size={18} />
                  <span>Biletlerim</span>
                </button>
                <button
                  onClick={() => {
                    handleLinkClick();
                    navigate('/my-favorites');
                  }}
                  className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 rounded-lg flex items-center gap-3 transition-colors"
                >
                  <Icon name="Heart" size={18} />
                  <span>Favorilerim</span>
                </button>
                <div className="pt-1 border-t border-gray-100">
                  <button
                    onClick={() => {
                      handleLinkClick();
                      handleLogout();
                    }}
                    className="w-full px-4 py-3 text-left text-sm text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-3 transition-colors"
                  >
                    <Icon name="LogOut" size={18} />
                    <span>Çıkış Yap</span>
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => {
                  handleLinkClick();
                  handleLogin();
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-500 text-white hover:from-blue-600 hover:to-cyan-600 transition-all duration-200 shadow-md font-medium"
              >
                <Icon name="LogIn" size={18} />
                <span>Giriş Yap</span>
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Global Search Modal */}
      <GlobalSearch isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />

      {/* Global Cart Drawer - accessible from all pages */}
      <CartDrawer
        isOpen={isCartOpen}
        onClose={closeCart}
        cartItems={cartItems}
        onUpdateQuantity={updateQuantity}
        onRemoveItem={removeFromCart}
        onCheckout={() => {
          closeCart();
          window.location.href = '/checkout';
        }}
      />
    </>
  );
};

export default Header;